import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AgentFindInterface } from '@zaoblako/shared/modules/ai/dtos/agent/agent-find.dto';
import { AgentFormDto, AgentFormInterface } from '@zaoblako/shared/modules/ai/dtos/agent/agent-form.dto';
import { AgentOperatingModeEnum } from '@zaoblako/shared/modules/ai/enums/agent-operating-mode.enum';
import { AgentTypeEnum } from '@zaoblako/shared/modules/ai/enums/agent-type.enum';
import { KnowledgeBaseCategoryEnum } from '@zaoblako/shared/modules/ai/enums/knowledge-base-category.enum';
import { KnowledgeBaseCategoryEnumI18n } from '@zaoblako/shared/modules/ai/i18n/knowledge-base-category-enum.i18n';
import { I18nLocalesEnum } from '@zaoblako/shared/common/enums/i18n.locales.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { AiAgentService } from '@zaoblako/web/core/services/directory/ai-agent.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { EnumValuesMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/enum-values-multi-select/enum-values-multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { AgentOperatingModeNamePipe } from '@zaoblako/web/shared/pipes/agent-operating-mode-name.pipe';
import { AgentTypeNamePipe } from '@zaoblako/web/shared/pipes/agent-type-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { SliderModule } from 'primeng/slider';
import { Observable } from 'rxjs';
import { InputTextareaModule } from 'primeng/inputtextarea';

@Component({
  selector: 'crm-ai-agent-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    ButtonComponent,
    EnumValuesMultiSelectComponent,
    SingleSelectComponent,
    AgentOperatingModeNamePipe,
    AgentTypeNamePipe,
    SliderModule,
    FormsModule,
    CheckboxModule,
    InputTextareaModule
  ],
  templateUrl: './ai-agent-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiAgentFormComponent extends BaseFormComponent<AgentFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public aiAgent: AgentFormInterface = <any>{
    temperature: 0,
    minConfidence: 0,
    operatingMode: AgentOperatingModeEnum.FREE_MODE,
    isEmbeddingDefault: false
  };

  public dto = AgentFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public types = Object.values(AgentTypeEnum);
  public operatingModes = Object.values(AgentOperatingModeEnum);
  public searchCategoriesOptions = Object.values(KnowledgeBaseCategoryEnum).map((category) => ({
    name: KnowledgeBaseCategoryEnumI18n[I18nLocalesEnum.RU_RU][category],
    value: category
  }));

  public get needSearchCategories(): boolean {
    return this.aiAgent?.operatingMode === AgentOperatingModeEnum.FRM_MODE;
  }

  public constructor(
    private readonly aiAgentService: AiAgentService,
    private readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.aiAgentService
      .findById<AgentFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, { withApiKey: true })
      .subscribe({
        next: (aiAgent) => {
          this.aiAgent = { ...aiAgent, apiKey: aiAgent.apiKey ?? '' };
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveAgent() {
    if (this.checkValidate(this.aiAgent).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.aiAgentService.update<AgentFormInterface, AgentFindInterface>(this.aiAgent).subscribe({
          next: (aiAgent) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`ИИ агент "${this.aiAgent.name}" обновлен`);
            this.formStoreService.saveNext(aiAgent, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.aiAgentService.create<AgentFindInterface>(this.aiAgent).subscribe({
          next: (aiAgent) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('ИИ агент создан');
            this.formStoreService.saveNext(aiAgent, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteAgent() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить ИИ агента "${this.aiAgent.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.aiAgentService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`ИИ агент "${this.aiAgent.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public needFolderID() {
    return this.aiAgent.type === AgentTypeEnum.YANDEX || this.aiAgent.type === AgentTypeEnum.OPENAI;
  }

  public needBaseURL() {
    return this.aiAgent.type === AgentTypeEnum.OPENAI;
  }

  public onIsEmbeddingChange(enabled: boolean): void {
    if (!enabled) {
      this.aiAgent.isEmbeddingDefault = false;
    }
  }

  public restoreAgent() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить ИИ агента "${this.aiAgent.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.aiAgentService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.aiAgent = { ...this.aiAgent, deleted: false } as any;
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`ИИ агент "${this.aiAgent.name}" восстановлен`);
            this.formStoreService.saveNext(this.aiAgent, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
